/**
 * OrgChainBreadcrumb Component
 *
 * Ancestor navigation for the org panels, in real hierarchy order:
 * Company › Reseller › Location › current entity. Each ancestor navigates via
 * useOrgDetailLink (preserving the page's filter/tab params); the current
 * entity renders as the bold terminal crumb. Renders nothing when the entity
 * has no ancestors (e.g. the Company panel).
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Breadcrumb } from '@components/design-system';
import { useOrgDetailLink } from './useOrgDetailLink';
import { Selection } from './orgNavTypes';

export type OrgChainAncestor = {
    type: Selection;
    id: number;
    name: string;
};

export type OrgChainBreadcrumbProps = {
    /** Ancestors in top-down order (company first). Nullish entries are skipped. */
    ancestors: (OrgChainAncestor | null | undefined)[];
    /** Label of the entity whose panel is open. */
    current: string;
};

export const OrgChainBreadcrumb: React.FC<OrgChainBreadcrumbProps> = ({ ancestors, current }) => {
    const navigate = useNavigate();
    const detailLink = useOrgDetailLink();

    const presentAncestors = ancestors.filter(
        (ancestor): ancestor is OrgChainAncestor => Boolean(ancestor && ancestor.id && ancestor.name),
    );

    if (!presentAncestors.length) return null;

    return (
        <Breadcrumb
            items={[
                ...presentAncestors.map((ancestor) => ({
                    label: ancestor.name,
                    onClick: () => navigate(detailLink(ancestor.type, ancestor.id)),
                })),
                { label: current },
            ]}
            sx={{ mb: 1 }}
        />
    );
};

export default OrgChainBreadcrumb;
